var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { ClickAwayListener, Typography, } from '@material-ui/core';
import moment from 'moment';
import React, { forwardRef, useCallback, useEffect, useRef, useState, } from 'react';
import { DayPickerRangeController, } from 'react-dates';
import 'react-dates/initialize';
import Box from '../Box';
import DatePickerProvider from '../DateRangePicker/DatePickerProvider';
import Icon from '../Icon';
import { useTheme } from '../theme';
import SegmentedDateInput from './SegmentedDateInput';
/**
 * Kareo wrapped component of the react-dates library Date Range Picker
 *
 * Date Range Picker: [Docs](http://airbnb.io/react-dates), [API](https://www.npmjs.com/package/react-dates/)
 */
var FillableDateRangePicker = forwardRef(function (_a, ref) {
    var startValue = _a.startValue, endValue = _a.endValue, startLabel = _a.startLabel, endLabel = _a.endLabel, _b = _a.onStartChange, onStartChange = _b === void 0 ? function () { } : _b, _c = _a.onEndChange, onEndChange = _c === void 0 ? function () { } : _c, _d = _a.onChange, onChange = _d === void 0 ? function () { } : _d, maxDays = _a.maxDays, props = __rest(_a, ["startValue", "endValue", "startLabel", "endLabel", "onStartChange", "onEndChange", "onChange", "maxDays"]);
    var theme = useTheme();
    var _e = useState('startDate'), input = _e[0], setInput = _e[1];
    var _f = useState(false), open = _f[0], setOpen = _f[1];
    var _g = useState(''), errorMessage = _g[0], setErrorMessage = _g[1];
    var startInputRef = useRef(null);
    var endInputRef = useRef(null);
    var _h = useState(function () {
        if (startValue)
            return startValue.clone();
        if (endValue)
            return endValue.clone();
        return moment();
    }), currentMonth = _h[0], setCurrentMonth = _h[1];
    var _j = useState(0), calendarKey = _j[0], setCalendarKey = _j[1];
    // Add internal state to track current selection to avoid race conditions
    var _k = useState(startValue), internalStartDate = _k[0], setInternalStartDate = _k[1];
    var _l = useState(endValue), internalEndDate = _l[0], setInternalEndDate = _l[1];
    // Track the last interaction type
    var lastInteractionRef = useRef(null);
    // Sync internal state with props
    useEffect(function () {
        setInternalStartDate(startValue);
    }, [startValue]);
    useEffect(function () {
        setInternalEndDate(endValue);
    }, [endValue]);
    useEffect(function () {
        if (internalStartDate && internalEndDate) {
            if (internalStartDate.isAfter(internalEndDate)) {
                setErrorMessage('Start date must be before end date');
            }
            else if (maxDays &&
                internalEndDate.diff(internalStartDate, 'days') > maxDays) {
                setErrorMessage("Date range can't be more than " + maxDays + " days");
            }
            else {
                setErrorMessage('');
            }
        }
        else {
            setErrorMessage('');
        }
    }, [internalStartDate, internalEndDate, maxDays]);
    var startDateString = startValue ? startValue.format('MM/DD/YYYY') : '';
    var endDateString = endValue ? endValue.format('MM/DD/YYYY') : '';
    // Update current month when dates change
    useEffect(function () {
        if (input === 'startDate' && startValue) {
            setCurrentMonth(startValue.clone());
            setCalendarKey(function (prev) { return prev + 1; });
        }
        else if (input === 'endDate' && endValue) {
            setCurrentMonth(endValue.clone());
            setCalendarKey(function (prev) { return prev + 1; });
        }
    }, [startValue, endValue, input]);
    var handleDatesChange = useCallback(function (arg) {
        // Mark this as a calendar interaction
        lastInteractionRef.current = 'calendar';
        setInternalStartDate(arg.startDate);
        setInternalEndDate(arg.endDate);
        var startDateChanged = (arg.startDate === null && startValue !== null) ||
            (arg.startDate !== null && startValue === null) ||
            (arg.startDate !== null &&
                startValue !== null &&
                !arg.startDate.isSame(startValue, 'day'));
        var endDateChanged = (arg.endDate === null && endValue !== null) ||
            (arg.endDate !== null && endValue === null) ||
            (arg.endDate !== null &&
                endValue !== null &&
                !arg.endDate.isSame(endValue, 'day'));
        if (startDateChanged) {
            onStartChange(arg.startDate);
        }
        if (endDateChanged) {
            onEndChange(arg.endDate);
            setOpen(false);
            if (onChange) {
                onChange(arg.startDate, arg.endDate);
            }
        }
    }, [onEndChange, onStartChange, onChange, startValue, endValue]);
    var handleFocusChange = useCallback(function (focused) {
        setInput(!focused ? 'startDate' : focused);
    }, []);
    var handleStartDateChange = useCallback(function (value) {
        // Mark this as a typing interaction
        lastInteractionRef.current = 'typing';
        if (value === '' || value === '//') {
            setInternalStartDate(null);
            onStartChange(null);
            return;
        }
        var momentDate = moment(value, 'MM/DD/YYYY', true);
        if (momentDate.isValid() && momentDate.year() >= 1900) {
            setCurrentMonth(momentDate.clone());
            setCalendarKey(function (prev) { return prev + 1; });
            setInternalStartDate(momentDate);
            onStartChange(momentDate);
        }
    }, [onStartChange]);
    var handleEndDateChange = useCallback(function (value) {
        // Mark this as a typing interaction
        lastInteractionRef.current = 'typing';
        if (value === '' || value === '//') {
            setInternalEndDate(null);
            onEndChange(null);
            return;
        }
        var momentDate = moment(value, 'MM/DD/YYYY', true);
        if (momentDate.isValid() && momentDate.year() >= 1900) {
            setCurrentMonth(momentDate.clone());
            setCalendarKey(function (prev) { return prev + 1; });
            setInternalEndDate(momentDate);
            onEndChange(momentDate);
        }
    }, [onEndChange]);
    var handleStartInputClick = useCallback(function () {
        if (!open) {
            setOpen(true);
        }
        setInput('startDate');
    }, [open]);
    var handleEndInputClick = useCallback(function () {
        if (!open) {
            setOpen(true);
        }
        setInput('endDate');
    }, [open]);
    var handleClickAway = useCallback(function (event) {
        var _a, _b;
        var target = event.target;
        var clickedStartInput = (_a = startInputRef.current) === null || _a === void 0 ? void 0 : _a.contains(target);
        var clickedEndInput = (_b = endInputRef.current) === null || _b === void 0 ? void 0 : _b.contains(target);
        if (!clickedStartInput && !clickedEndInput) {
            setOpen(false);
        }
    }, []);
    var handleComponentBlur = useCallback(function (event) {
        var _a, _b;
        var relatedTarget = event.relatedTarget;
        var focusStayingInStartInput = (_a = startInputRef.current) === null || _a === void 0 ? void 0 : _a.contains(relatedTarget);
        var focusStayingInEndInput = (_b = endInputRef.current) === null || _b === void 0 ? void 0 : _b.contains(relatedTarget);
        // Only call onChange on blur if the last interaction was typing, not calendar clicking
        if (!focusStayingInStartInput &&
            !focusStayingInEndInput &&
            lastInteractionRef.current === 'typing' &&
            onChange) {
            onChange(internalStartDate, internalEndDate);
        }
        // Reset the interaction type after blur
        lastInteractionRef.current = null;
    }, [onChange, internalStartDate, internalEndDate]);
    return (React.createElement(DatePickerProvider, { style: { position: 'relative' } },
        React.createElement(ClickAwayListener, { onClickAway: handleClickAway },
            React.createElement("div", { style: { position: 'relative' } },
                React.createElement(Box, { display: "inline-flex", alignItems: "center" },
                    React.createElement(Box, { marginRight: 1, ref: startInputRef, onBlur: handleComponentBlur },
                        React.createElement(SegmentedDateInput, { label: !startLabel ? '' : startLabel, value: startDateString, onChange: handleStartDateChange, onClick: handleStartInputClick, error: !!errorMessage })),
                    React.createElement(Box, { ref: endInputRef, onBlur: handleComponentBlur },
                        React.createElement(SegmentedDateInput, { label: !endLabel ? '' : endLabel, value: endDateString, onChange: handleEndDateChange, onClick: handleEndInputClick, error: !!errorMessage }))),
                !!errorMessage && (React.createElement(Typography, { component: "div", variant: "caption", color: "error", style: { marginTop: '4px', marginLeft: '8px' } }, errorMessage)),
                open ? (React.createElement(Box, { component: "div", position: "absolute", left: 0, zIndex: theme.zIndex.modal, style: { marginTop: '4px' } },
                    input === 'startDate' ? (React.createElement(Box, { marginBottom: -2.4, marginLeft: 1.1, marginTop: 1 },
                        React.createElement(Icon, null,
                            React.createElement("path", { d: "M0,10 10,0 20,10", fill: theme.palette.grey[300], stroke: theme.palette.grey[300] })))) : (React.createElement(Box, { marginBottom: -2.4, marginLeft: 29.2, marginTop: 1 },
                        React.createElement(Icon, null,
                            React.createElement("path", { d: "M0,10 10,0 20,10", fill: theme.palette.grey[300], stroke: theme.palette.grey[300] })))),
                    React.createElement(DayPickerRangeController, __assign({}, props, { ref: ref, key: calendarKey, focusedInput: input, onFocusChange: handleFocusChange, startDate: internalStartDate, endDate: internalEndDate, onDatesChange: handleDatesChange, initialVisibleMonth: function () {
                            return currentMonth;
                        }, onPrevMonthClick: function (newMonth) {
                            setCurrentMonth(newMonth);
                        }, onNextMonthClick: function (newMonth) {
                            setCurrentMonth(newMonth);
                        }, hideKeyboardShortcutsPanel: true })))) : null))));
});
FillableDateRangePicker.displayName = 'KareoFillableDateRangePicker';
export default FillableDateRangePicker;
